import React, { useState, useContext, useEffect } from 'react';
import {
	TextField,
	Button,
	Paper,
	Typography,
	MenuItem,
	InputLabel,
	FormControl,
	Select,
	Switch,
	FormControlLabel,
	Box,
	Stack
} from '@mui/material';
import SaveRoundedIcon from '@mui/icons-material/SaveRounded';
import EditRoundedIcon from '@mui/icons-material/EditRounded';

import { useNavigate, useParams } from 'react-router-dom';
import { DataContext } from '../../../context/DataContext';
import { getRuta, isAccess, simpleMessage } from '../../../helpers/Helpers';
import { getToken } from '../../../api/Account';
import { getRolByIdAsync, getRolListAsync, updateRolAsync } from '../../../api/Rols';
import { MODULES, ALL_PERMISSIONS } from './rolModules';
import PermissionsPicker from './PermissionsPicker';
import BackButton from '../../../components/ui/BackButton';

const EditRol = () => {
	const token = getToken();
	let ruta = getRuta();
	let navigate = useNavigate();
	const { id } = useParams();

	const { setIsLoading, setIsUnautorized, access } = useContext(DataContext);

	const [rolName, setRolName] = useState('');
	const [isFullAccess, setIsFullAccess] = useState(false);
	const [rolesList, setRolesList] = useState([]);
	const [selectedRol, setSelectedRol] = useState(null);
	const [nivelJerarquico, setNivelJerarquico] = useState(0);
	const [isEdit, setIsEdit] = useState(false);

	const [permissions, setPermissions] = useState(
		Object.fromEntries(ALL_PERMISSIONS.map(({ description }) => [description, false]))
	);

	const handleChangeFullAccess = () => {
		setIsFullAccess((prev) => {
			const nuevoValor = !prev;
			setPermissions((previo) =>
				Object.fromEntries(Object.keys(previo).map((p) => [p, nuevoValor]))
			);
			return nuevoValor;
		});
	};

	const saveRol = async () => {
		if (!rolName.trim()) {
			simpleMessage('Ingrese un nombre para el rol', 'error');
			return;
		}

		// Se reconstruye la lista completa desde el catalogo en vez de mutar solo
		// los permisos que el rol ya traia. Asi un rol guardado antes de que el
		// catalogo se corrigiera queda saneado al reguardarlo: recupera las claves
		// que le faltaban ('SECURITY CREATE', 'MIGRAR CARTERA', las de sucursal y
		// 'REP UTRACK') conservando el id de las que ya existian.
		const previos = new Map(
			(selectedRol.permissions || []).map((p) => [p.description, p])
		);

		const datos = {
			...selectedRol,
			rolName: rolName.trim(),
			isFullAccess,
			idRolSuperior: nivelJerarquico,
			permissions: ALL_PERMISSIONS.map(({ description }) => {
				const previo = previos.get(description);
				return previo
					? { ...previo, isEnable: permissions[description] }
					: { description, isEnable: permissions[description] };
			})
		};

		setIsLoading(true);
		const result = await updateRolAsync(token, datos);
		if (!result.statusResponse) {
			setIsLoading(false);

			if (result.error === 'Unauthorized') {
				setIsUnautorized(true);
				return;
			}

			simpleMessage(result.error, 'error');
			return;
		}
		setIsLoading(false);
		setSelectedRol(datos);
		simpleMessage('Rol actualizado...!', 'success');
		setIsEdit(false);
	};

	const getRolData = async () => {
		setIsLoading(true);
		const result = await getRolByIdAsync(token, id);
		if (!result.statusResponse) {
			setIsLoading(false);
			if (result.error === 'Unauthorized') {
				setIsUnautorized(true);
				return;
			}
			simpleMessage(result.error, 'error');
			return;
		}

		const rol = result.data.result;
		setSelectedRol(rol);
		setRolName(rol.rolName);
		setNivelJerarquico(rol.idRolSuperior);
		setIsFullAccess(rol.isFullAccess);

		// Los permisos que el rol no tenga guardados arrancan en false.
		const guardados = Object.fromEntries(
			(rol.permissions || []).map((p) => [p.description, !!p.isEnable])
		);
		setPermissions(
			Object.fromEntries(
				ALL_PERMISSIONS.map(({ description }) => [description, !!guardados[description]])
			)
		);

		setIsLoading(false);
	};

	useEffect(() => {
		(async () => {
			await getRolData();

			const result = await getRolListAsync(token);
			if (!result.statusResponse) {
				if (result.error === 'Unauthorized') {
					setIsUnautorized(true);
					return;
				}
				simpleMessage(result.error, 'error');
				return;
			}
			setRolesList(result.data.result || []);
		})();
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [id]);

	return (
		<div>
			<Paper sx={{ overflow: 'hidden' }}>
				<Stack
					direction={{ xs: 'column', sm: 'row' }}
					alignItems={{ xs: 'stretch', sm: 'center' }}
					justifyContent="space-between"
					spacing={2}
					sx={{ padding: '18px 22px', borderBottom: '1px solid', borderColor: 'divider' }}
				>
					<Stack direction="row" alignItems="center" spacing={2} sx={{ minWidth: 0 }}>
						<BackButton onClick={() => navigate(`${ruta}/rols`)} />
						<Box sx={{ minWidth: 0 }}>
							<Typography
								sx={{
									fontFamily: "'Poppins', sans-serif",
									fontWeight: 600,
									fontSize: 16.5,
									color: 'text.primary',
									lineHeight: 1.1
								}}
							>
								{rolName}
							</Typography>
							<Typography sx={{ fontSize: 13, color: 'text.disabled', marginTop: '3px' }}>
								Permisos del rol
							</Typography>
						</Box>
					</Stack>

					{isAccess(access, 'SECURITY UPDATE') ? (
						<Button
							onClick={() => setIsEdit(!isEdit)}
							variant={isEdit ? 'outlined' : 'contained'}
							color="primary"
							startIcon={<EditRoundedIcon />}
							sx={{ whiteSpace: 'nowrap', height: '44px' }}
						>
							{isEdit ? 'Cancelar edición' : 'Editar'}
						</Button>
					) : null}
				</Stack>

				<Box sx={{ padding: '22px' }}>
					<Stack
						spacing={2}
						direction={{ xs: 'column', sm: 'row' }}
						justifyContent="space-between"
						sx={{ marginBottom: '18px' }}
					>
						<TextField
							fullWidth
							size="small"
							disabled={!isEdit}
							onChange={(e) => setRolName(e.target.value.toUpperCase())}
							label={'Nombre del rol'}
							value={rolName}
						/>

						<FormControl fullWidth size="small" disabled={!isEdit}>
							<InputLabel id="rol-superior-label">Rol superior</InputLabel>
							<Select
								labelId="rol-superior-label"
								value={nivelJerarquico}
								onChange={(e) => setNivelJerarquico(e.target.value)}
								label="Rol superior"
								style={{ textAlign: 'left' }}
							>
								<MenuItem value={0}>
									<em>Seleccione un rol superior</em>
								</MenuItem>
								{rolesList.map((item) => (
									<MenuItem key={item.id} value={item.id}>
										{item.rolName}
									</MenuItem>
								))}
							</Select>
						</FormControl>
					</Stack>

					<Stack
						direction="row"
						alignItems="center"
						spacing={1}
						sx={{ marginBottom: '18px' }}
					>
						<Typography sx={{ fontSize: 13.5, fontWeight: 700, color: 'text.secondary' }}>
							Acceso total al sistema
						</Typography>
						<FormControlLabel
							control={
								<Switch
									checked={isFullAccess}
									disabled={!isEdit}
									onChange={handleChangeFullAccess}
								/>
							}
							label={isFullAccess ? 'Activado' : 'Desactivado'}
						/>
					</Stack>

					<PermissionsPicker
						modules={MODULES}
						permissions={permissions}
						onToggle={(desc) =>
							isEdit && setPermissions((prev) => ({ ...prev, [desc]: !prev[desc] }))
						}
						onToggleMany={(descs, valor) =>
							isEdit &&
							setPermissions((prev) => {
								const next = { ...prev };
								descs.forEach((d) => {
									next[d] = valor;
								});
								return next;
							})
						}
						readOnly={!isEdit}
					/>

					{isEdit ? (
						<Button
							variant="contained"
							color="primary"
							sx={{ marginTop: '20px' }}
							startIcon={<SaveRoundedIcon />}
							onClick={saveRol}
						>
							Guardar cambios
						</Button>
					) : null}
				</Box>
			</Paper>
		</div>
	);
};

export default EditRol;
